/**
 * Cognito sign-in screen (Slice AUTH). Email + password via Amplify; shown by
 * the App gate whenever there's no authenticated session.
 *
 * Invitation-only: a banner points would-be users at info@sigongroup.com, and
 * the "Forgot password?" link only appears once an APPROVED email is typed.
 * That allowlist is UX only — Cognito (admin-created users, self-signup off) is
 * the real boundary. Resetting uses Cognito's email-code flow.
 */
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Form,
  FormField,
  Header,
  Input,
  Link,
  SpaceBetween,
} from "@cloudscape-design/components";
import { isApprovedEmail, useAuth } from "@/core/auth";

type Mode = "signin" | "reset";

export function Login() {
  const { signIn, requestPasswordReset, confirmPasswordReset } = useAuth();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const approved = isApprovedEmail(email);

  const doSignIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const startReset = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await requestPasswordReset(email);
      setMode("reset");
      setNotice(`We emailed a code to ${email.trim()}. Enter it below with a new password.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const doConfirmReset = async () => {
    setBusy(true);
    setError(null);
    try {
      await confirmPasswordReset(email, code, newPassword);
      setMode("signin");
      setPassword("");
      setCode("");
      setNewPassword("");
      setNotice("Password updated — sign in with your new password.");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const backToSignIn = () => {
    setMode("signin");
    setError(null);
    setNotice(null);
    setCode("");
    setNewPassword("");
  };

  return (
    <div className="sigon-login">
      <div className="sigon-login-inner">
        <SpaceBetween size="l">
          <div className="sigon-login-mark">
            <span className="sigon-login-row">
              <img src="/brand/sigon-logo.png" alt="Sigon" />
              <span className="sigon-login-brand">Sigon</span>
            </span>
            <span className="sigon-login-product">SOC1.lab</span>
            <p className="sigon-login-tagline">
              AI-native SOC 1 Type II review for in-house SOX teams
            </p>
          </div>

          <Container
            header={
              <Header variant="h2">{mode === "signin" ? "Sign in" : "Reset your password"}</Header>
            }
          >
            {mode === "signin" ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (email && password && !busy) void doSignIn();
                }}
              >
                <Form
                  actions={
                    <Button variant="primary" loading={busy} disabled={!email || !password || busy}>
                      Sign in
                    </Button>
                  }
                >
                  <SpaceBetween size="m">
                    {error && (
                      <Alert type="error" header="Sign-in failed">
                        {error}
                      </Alert>
                    )}
                    {notice && <Alert type="success">{notice}</Alert>}
                    <FormField label="Email">
                      <Input
                        type="email"
                        value={email}
                        onChange={({ detail }) => setEmail(detail.value)}
                        autoFocus
                        disabled={busy}
                      />
                    </FormField>
                    <FormField label="Password">
                      <Input
                        type="password"
                        value={password}
                        onChange={({ detail }) => setPassword(detail.value)}
                        disabled={busy}
                      />
                    </FormField>
                    {approved && (
                      <Box>
                        <Link
                          onFollow={(e) => {
                            e.preventDefault();
                            if (!busy) void startReset();
                          }}
                        >
                          Forgot password?
                        </Link>
                      </Box>
                    )}
                  </SpaceBetween>
                </Form>
              </form>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (code && newPassword && !busy) void doConfirmReset();
                }}
              >
                <Form
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button onClick={backToSignIn} disabled={busy}>
                        Back to sign in
                      </Button>
                      <Button
                        variant="primary"
                        loading={busy}
                        disabled={!code || !newPassword || busy}
                      >
                        Set new password
                      </Button>
                    </SpaceBetween>
                  }
                >
                  <SpaceBetween size="m">
                    {error && (
                      <Alert type="error" header="Reset failed">
                        {error}
                      </Alert>
                    )}
                    {notice && <Alert type="info">{notice}</Alert>}
                    <FormField label="Verification code">
                      <Input
                        value={code}
                        onChange={({ detail }) => setCode(detail.value)}
                        autoFocus
                        disabled={busy}
                        inputMode="numeric"
                      />
                    </FormField>
                    <FormField
                      label="New password"
                      description="At least 12 characters, with an uppercase letter, a lowercase letter, and a number."
                    >
                      <Input
                        type="password"
                        value={newPassword}
                        onChange={({ detail }) => setNewPassword(detail.value)}
                        disabled={busy}
                      />
                    </FormField>
                  </SpaceBetween>
                </Form>
              </form>
            )}
          </Container>

          <Box textAlign="center" variant="small" color="text-body-secondary">
            Invitation-only — access is limited to authorized Sigon users. Email{" "}
            <Link href="mailto:info@sigongroup.com" external={false}>
              info@sigongroup.com
            </Link>{" "}
            to request access.
          </Box>
        </SpaceBetween>
      </div>
    </div>
  );
}
